// Shared JWT helper — canonical location for the direct-localStorage
// JWT read pattern.
//
// Every view that authenticates a fetch call to /api/* should use
// bearer() from here instead of a local supabase.auth.getSession()
// call. getSession() can hang mid-refresh on a stale token —
// documented root cause of ~15 "Loading X…" stuck-loading regressions
// caught in the 2026-07 smoke test sweep. This helper decodes the JWT
// locally, checks the exp claim, and returns the token as an
// Authorization header if it's fresh — with no network round trip.
//
// If the JWT is missing or stale, bearer() returns an empty object;
// the fetch will 401 and the view should render an amber error
// instead of hanging on loading=true forever.
//
// Related implementations that predate this consolidation:
//   src/leads/authedFetch.js       — lead panels
//   src/referrals/_authedFetch.js  — referral panels
// Both inline the same logic; long-term they should re-export from
// here. For now they coexist to avoid a large diff.

// Decode a base64url-encoded JWT segment (the payload). Supabase v2
// SDKs sign JWTs with base64url encoding (- and _ instead of + and /),
// so raw atob() throws on such tokens. Fix per 2026-07-13 outage:
// SupportLookupView + any consumer of authedFetch was silently 401'ing
// because atob threw on the first VA token in localStorage → outer
// try/catch caught it → loop exited returning null → no auth header
// on every fetch. Normalize the alphabet + pad before decoding.
function b64urlDecode(seg) {
  const s = String(seg || '').replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (s.length % 4)) % 4;
  return atob(s + '='.repeat(padLen));
}

export function readPersistedJwtDirect() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  // Per-key try so a malformed row can't blow up the whole loop —
  // previously ONE broken token in localStorage prevented finding any
  // other valid tokens further down the loop.
  for (let i = 0; i < localStorage.length; i++) {
    try {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('sb-') || !k.endsWith('-auth-token')) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const token = parsed?.access_token || parsed?.currentSession?.access_token;
      if (!token) continue;
      const payload = JSON.parse(b64urlDecode(token.split('.')[1] || ''));
      if (payload?.exp && payload.exp * 1000 > Date.now() + 60_000) return token;
    } catch { /* skip this key, keep scanning */ }
  }
  return null;
}

export async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Fetch wrapper that carries the bearer header, JSON-parses the
// response defensively (handles plain-text 500 pages), and applies
// an AbortController timeout. Returns { ok, data, error, status }.
//
// Usage:
//   const { ok, data, error } = await authedFetch('/api/foo', { body: {...} });
//   if (!ok) return <ErrorBox msg={error}/>;
export async function authedFetch(url, opts = {}, externalSignal = null) {
  const {
    method = 'GET',
    body,
    timeoutMs = 12_000,
    headers: extraHeaders = {},
  } = opts;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const signal = externalSignal
    ? anyOf([controller.signal, externalSignal])
    : controller.signal;

  try {
    const res = await fetch(url, {
      method,
      signal,
      headers: {
        'content-type': 'application/json',
        ...(await bearer()),
        ...extraHeaders,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const raw = await res.text();
    let json = null;
    try { json = raw ? JSON.parse(raw) : null; } catch { /* non-JSON body */ }
    if (res.ok && (json?.ok !== false)) {
      return { ok: true, data: json, error: null, status: res.status };
    }
    const err = json?.error || `HTTP ${res.status}${raw && !json ? ` · ${raw.slice(0, 120)}` : ''}`;
    return { ok: false, data: null, error: err, status: res.status };
  } catch (err) {
    const msg = err?.name === 'AbortError' ? 'request_timed_out' : (err?.message || 'load_failed');
    return { ok: false, data: null, error: msg, status: 0 };
  } finally {
    clearTimeout(timer);
  }
}

// Merge multiple AbortSignals — the fetch aborts if ANY of them abort.
// Minimal polyfill for the older browser AbortSignal.any().
function anyOf(signals) {
  const controller = new AbortController();
  for (const s of signals) {
    if (s.aborted) { controller.abort(); break; }
    s.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return controller.signal;
}
